import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Inbox, RefreshCw, Mail, Building2, Download, BarChart3, MessageSquare, Send } from "lucide-react";
import { adminLogin, fetchMe, fetchEnquiries, updateEnquiryStatus, replyEnquiry, fetchPrototypeStats, fetchInvestorQuestions, clearToken, getToken, apiErr } from "../lib/api";
import { PROTOTYPES } from "../lib/site";

const STATUSES = ["New", "Contacted", "Closed"];
const STATUS_STYLE = {
  New: "bg-[#6366F1] text-[#0E1117]",
  Contacted: "bg-[#A5B4FC] text-[#0E1117]",
  Closed: "bg-white/10 text-[#9DA9BA]",
};
const TEMPLATES = [
  { label: "Book a demo", text: "Thanks for your interest in PARTECON! I'd love to walk you through the platform. Reply with a couple of times that suit you and I'll set up a 30-minute demo tailored to your school." },
  { label: "Send deck", text: "Thank you for reaching out. I'm sharing our PARTECON overview so you can explore the product, and I'm happy to answer any questions or arrange a walkthrough." },
  { label: "Follow up", text: "Just following up on your enquiry with PARTECON — let me know a good time to connect and I'll set up a quick call." },
];

const field = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3.5 text-sm text-white placeholder:text-[#9DA9BA]/60 focus:border-[#6366F1]/50 focus:outline-none transition-colors";

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      onDone();
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 radial-glow">
      <form onSubmit={submit} className="glass rounded-3xl p-8 md:p-10 w-full max-w-md" data-testid="admin-login-form">
        <img src="/partecon-logo.png" alt="PARTECON" className="h-16 w-auto mb-5" />
        <span className="overline">PARTECON · Admin</span>
        <h1 className="font-heading text-3xl font-medium tracking-tight mt-3">Enquiry Inbox</h1>
        <p className="text-[#9DA9BA] mt-2 text-sm">Sign in to review demo and pilot requests.</p>
        <div className="space-y-3 mt-7">
          <input data-testid="admin-email" type="email" className={field} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={field} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="btn-primary w-full py-3.5 text-sm disabled:opacity-60">{loading ? "Signing in…" : "Sign In"}</button>
        </div>
      </form>
    </div>
  );
}

function Inbox2({ onLogout }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [replyId, setReplyId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const text = replyText.trim();
      await replyEnquiry(id, text);
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status: "Contacted", replies: [...(x.replies || []), { message: text, at: new Date().toISOString() }] } : x)));
      setReplyId(null); setReplyText("");
      toast.success("Reply sent to the lead.");
    } catch (err) { toast.error(apiErr(err)); } finally { setSending(false); }
  };

  const exportQuestionsCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Question", "Answer", "Asked"].join(",")].concat(
      questions.map((x) => [x.question, x.answer, x.created_at].map(esc).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `partecon-investor-questions-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [enq, st, qs] = await Promise.all([fetchEnquiries(), fetchPrototypeStats(), fetchInvestorQuestions()]);
      setRows(enq.data);
      setStats(st.data);
      setQuestions(qs.data);
    } catch (err) {
      toast.error(apiErr(err));
      if (err?.response?.status === 401) onLogout();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (id, status) => {
    const prev = rows;
    setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await updateEnquiryStatus(id, status);
      toast.success(`Marked as ${status}`);
    } catch (err) {
      setRows(prev);
      toast.error(apiErr(err));
    }
  };

  const exportCsv = () => {
    const headers = ["Name", "Email", "School", "Role", "Interest", "Source", "Status", "Message", "Created"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")].concat(
      rows.map((r) => [r.name, r.email, r.school, r.role, r.interest, r.source, r.status, r.message, r.created_at].map(esc).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `partecon-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sourceTag = (s) => ({ "investor-deck": "Deck Access", "investor-access": "Investor Access", talk: "Talk", contact: "Contact", demo: "Demo", website: "Demo" }[s] || "Enquiry");
  const inSource = (r) => {
    if (sourceFilter === "All") return true;
    if (sourceFilter === "Deck Access") return r.source === "investor-deck";
    if (sourceFilter === "Investor Access") return r.source === "investor-access";
    if (sourceFilter === "Enquiries") return !["investor-deck", "investor-access"].includes(r.source);
    return true;
  };
  const shown = rows.filter((r) => (filter === "All" || r.status === filter) && inSource(r));
  const labelFor = (k) => (PROTOTYPES.find((p) => p.key === k)?.tag) || k;
  const sortedStats = [...stats].sort((a, b) => b.count - a.count);
  const maxCount = Math.max(1, ...stats.map((s) => s.count));
  const totalOpens = stats.reduce((n, s) => n + s.count, 0);

  return (
    <div className="min-h-screen pt-28 pb-20 px-5 sm:px-8" data-testid="admin-inbox">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="overline">PARTECON · Admin</span>
            <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-2 flex items-center gap-3">
              <Inbox size={26} className="text-[#6366F1]" /> Enquiry Inbox
            </h1>
            <p className="text-[#9DA9BA] mt-2 text-sm">{rows.length} enquir{rows.length === 1 ? "y" : "ies"} received</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={exportCsv} data-testid="admin-export" className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2"><Download size={15} /> Export CSV</button>
            <button onClick={load} data-testid="admin-refresh" className="btn-ghost px-5 py-2.5 text-sm inline-flex items-center gap-2"><RefreshCw size={15} /> Refresh</button>
            <button onClick={onLogout} data-testid="admin-logout" className="btn-ghost px-5 py-2.5 text-sm inline-flex items-center gap-2"><LogOut size={15} /> Logout</button>
          </div>
        </div>

        <div className="flex gap-2 mt-7 flex-wrap">
          {["All", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
              className={`text-xs font-mono-accent uppercase tracking-wider rounded-full px-4 py-1.5 border transition-colors ${filter === s ? "border-[#6366F1] text-[#6366F1]" : "border-white/10 text-[#9DA9BA] hover:text-white"}`}>
              {s}{s !== "All" ? ` (${rows.filter((r) => r.status === s).length})` : ` (${rows.length})`}
            </button>
          ))}
        </div>

        <div className="flex gap-2 mt-3 flex-wrap" data-testid="source-filters">
          {["All", "Enquiries", "Investor Access", "Deck Access"].map((s) => {
            const count = s === "All" ? rows.length
              : s === "Deck Access" ? rows.filter((r) => r.source === "investor-deck").length
              : s === "Investor Access" ? rows.filter((r) => r.source === "investor-access").length
              : rows.filter((r) => !["investor-deck", "investor-access"].includes(r.source)).length;
            return (
              <button key={s} onClick={() => setSourceFilter(s)} data-testid={`source-filter-${s.toLowerCase().replace(/ /g, "-")}`}
                className={`text-xs font-mono-accent uppercase tracking-wider rounded-full px-4 py-1.5 border transition-colors ${sourceFilter === s ? "border-[#A5B4FC] text-[#A5B4FC]" : "border-white/10 text-[#9DA9BA] hover:text-white"}`}>
                {s} ({count})
              </button>
            );
          })}
        </div>

        <div className="glass rounded-2xl p-6 md:p-7 mt-6" data-testid="proto-analytics">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-lg font-medium flex items-center gap-2.5"><BarChart3 size={19} className="text-[#6366F1]" /> Prototype Analytics</h2>
            <span className="font-mono-accent text-xs text-[#9DA9BA]">{totalOpens} total opens</span>
          </div>
          <div className="mt-5 space-y-3">
            {sortedStats.map((s) => (
              <div key={s.key} className="flex items-center gap-4" data-testid={`stat-${s.key}`}>
                <span className="w-32 shrink-0 text-sm text-white">{labelFor(s.key)}</span>
                <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#6366F1] to-[#F5B93C] transition-all duration-700" style={{ width: `${(s.count / maxCount) * 100}%` }} />
                </div>
                <span className="w-8 text-right font-mono-accent text-sm text-[#6366F1]">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6 md:p-7 mt-6" data-testid="investor-questions">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-lg font-medium flex items-center gap-2.5"><MessageSquare size={19} className="text-[#6366F1]" /> Investor Questions</h2>
            <div className="flex items-center gap-3">
              <span className="font-mono-accent text-xs text-[#9DA9BA]">{questions.length} asked</span>
              {questions.length > 0 && <button onClick={exportQuestionsCsv} data-testid="questions-export" className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5"><Download size={13} /> CSV</button>}
            </div>
          </div>
          {questions.length === 0 ? (
            <p className="text-[#9DA9BA] text-sm mt-4">No investor questions asked yet.</p>
          ) : (
            <div className="mt-5 space-y-4 max-h-96 overflow-auto pr-1">
              {questions.map((qq) => (
                <div key={qq.id} className="border-l-2 border-[#6366F1]/30 pl-3" data-testid="question-row">
                  <p className="text-white text-sm font-medium">{qq.question}</p>
                  {qq.answer && <p className="text-[#9DA9BA] text-xs mt-1.5 leading-relaxed">{qq.answer}</p>}
                  <span className="font-mono-accent text-[10px] text-[#9DA9BA]/60">{new Date(qq.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass rounded-2xl mt-6 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#9DA9BA]">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="p-12 text-center text-[#9DA9BA]">No enquiries{filter !== "All" ? ` marked ${filter}` : ""} yet.</div>
          ) : (
            <div className="divide-y divide-white/10">
              {shown.map((r) => (
                <div key={r.id} className="p-6 hover:bg-white/[0.03] transition-colors" data-testid="enquiry-row">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-white font-medium">{r.name}
                        <span className="ml-3 text-xs font-mono-accent uppercase tracking-wider text-[#9DA9BA] border border-white/10 rounded-full px-2.5 py-0.5">{r.interest || "Enquiry"}</span>
                        <span className={`ml-2 text-[10px] font-mono-accent uppercase tracking-wider rounded-full px-2.5 py-0.5 ${r.source === "investor-deck" ? "bg-[#6366F1] text-[#0E1117]" : r.source === "investor-access" ? "bg-[#A5B4FC] text-[#0E1117]" : "text-[#9DA9BA] border border-white/10"}`} data-testid="source-badge">{sourceTag(r.source)}</span>
                      </p>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-[#9DA9BA]">
                        <span className="inline-flex items-center gap-1.5"><Mail size={13} /> {r.email}</span>
                        {r.school && <span className="inline-flex items-center gap-1.5"><Building2 size={13} /> {r.school}</span>}
                        {r.role && <span>· {r.role}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-mono-accent uppercase tracking-wider rounded-full px-2.5 py-1 ${STATUS_STYLE[r.status] || STATUS_STYLE.New}`}>{r.status}</span>
                      <span className="font-mono-accent text-xs text-[#9DA9BA]/70 hidden sm:block">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {r.message && <p className="text-white/80 text-sm mt-3 leading-relaxed border-l-2 border-[#6366F1]/30 pl-3">{r.message}</p>}
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-[#9DA9BA]/70 mr-1">Set status:</span>
                    {STATUSES.map((s) => (
                      <button key={s} onClick={() => changeStatus(r.id, s)} data-testid={`status-${s.toLowerCase()}-${r.id}`}
                        className={`text-xs rounded-full px-3 py-1 border transition-colors ${r.status === s ? "border-[#6366F1] text-[#6366F1]" : "border-white/10 text-[#9DA9BA] hover:text-white hover:border-white/30"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                  {(r.replies && r.replies.length > 0) && (
                    <div className="mt-3 space-y-2" data-testid="reply-history">
                      {r.replies.map((rep, idx) => (
                        <div key={idx} className="text-xs bg-white/5 rounded-lg px-3 py-2 border-l-2 border-[#A5B4FC]/40">
                          <p className="text-white/80 whitespace-pre-wrap">{rep.message}</p>
                          <span className="font-mono-accent text-[10px] text-[#9DA9BA]/60">Sent {new Date(rep.at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3">
                    {replyId === r.id ? (
                      <div className="space-y-2" data-testid="reply-box">
                        <div className="flex flex-wrap gap-2">
                          {TEMPLATES.map((t) => (
                            <button key={t.label} onClick={() => setReplyText(t.text)} data-testid={`reply-template-${t.label.toLowerCase().replace(/ /g, "-")}`} className="text-[11px] text-[#A5B4FC] border border-white/10 hover:border-[#6366F1]/40 rounded-full px-2.5 py-1 transition-colors">{t.label}</button>
                          ))}
                        </div>
                        <textarea data-testid="reply-text" value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3} placeholder={`Reply to ${r.email}…`} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#9DA9BA]/60 focus:border-[#6366F1]/50 focus:outline-none" />
                        <div className="flex gap-2">
                          <button onClick={() => sendReply(r.id)} disabled={sending} data-testid="reply-send" className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60">{sending ? "Sending…" : <>Send Reply <Send size={13} /></>}</button>
                          <button onClick={() => { setReplyId(null); setReplyText(""); }} className="btn-ghost px-4 py-2 text-xs">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setReplyId(r.id); setReplyText(""); }} data-testid="reply-btn" className="text-xs text-[#6366F1] hover:underline inline-flex items-center gap-1.5"><Mail size={13} /> Reply</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null); // null=checking, true, false

  useEffect(() => {
    if (!getToken()) { setAuth(false); return; }
    fetchMe().then(() => setAuth(true)).catch(() => { clearToken(); setAuth(false); });
  }, []);

  const logout = () => { clearToken(); setAuth(false); };

  if (auth === null) return <div className="min-h-screen flex items-center justify-center text-[#9DA9BA]">Loading…</div>;
  if (!auth) return <Login onDone={() => setAuth(true)} />;
  return <Inbox2 onLogout={logout} />;
}
